import React from "react";
import {
  Loader,
  LoaderSizes,
  Button,
  Iconography,
  IconographySizes,
  Pagination,
  Notification,
  NotificationVariants,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../redux";
import { userSliceAction } from "../../../../../redux/slices/user";
import DeleteModal from "../../../../../common/components/DeleteModal";
import { ReactComponent as NoUserStateSVG } from "../../../../../common/assets/icons/emptystateuser.svg";
import {
  getUsersByTypeSagaAction,
  deleteUserSagaAction,
  sendAccountInviteSagaAction,
} from "../../../../../redux/saga/user/saga-actions";
import { UserListType } from "../../../../../redux/slices/user/Types";
import UserManagementToolbar from "../components/UserManagementToolbar";
import UserManagementList from "../components/UserManagementList";
import HidUserFormModal from "../modals/HidUserFormModal";
import { useFilteredUserList } from "../hooks/useFilteredUserList";

interface HidUsersTabProps {
  readOnly?: boolean;
}

const HidUsersTab: React.FC<HidUsersTabProps> = ({ readOnly = false }) => {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [currentPageNumber, setCurrentPageNumber] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);
  const [addModalOpen, setAddModalOpen] = React.useState(false);
  const [updateModalOpen, setUpdateModalOpen] = React.useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState<UserListType[0] | null>(
    null
  );
  const [userToDelete, setUserToDelete] = React.useState<UserListType[0] | null>(
    null
  );
  const [toasts, setToasts] = React.useState<any[]>([]);

  const dispatch = useAppDispatch();
  const { userListHID, status } = useAppSelector((state) => state.user);

  React.useEffect(() => {
    dispatch(getUsersByTypeSagaAction("HID"));
  }, [dispatch]);

  React.useEffect(() => {
    setCurrentPageNumber(1);
  }, [searchTerm]);

  React.useEffect(() => {
    if (
      status.sendAccountInviteStatus !== "idle" &&
      status.sendAccountInviteMessage
    ) {
      const isSuccess = status.sendAccountInviteStatus === "success";
      setToasts([
        {
          title: isSuccess ? "Account Invite Sent" : "Error",
          message: status.sendAccountInviteMessage,
          variant: isSuccess
            ? NotificationVariants.Success
            : NotificationVariants.Error,
          icon: (
            <Iconography
              icon={isSuccess ? "check" : "ban"}
              size={IconographySizes.Medium}
            />
          ),
          expanded: true,
        },
      ]);
      dispatch(userSliceAction.resetSendAccountInviteStatus());
    }
  }, [
    status.sendAccountInviteStatus,
    status.sendAccountInviteMessage,
    dispatch,
  ]);

  const onAddNewUser = React.useCallback(() => {
    setUpdateModalOpen(false);
    setAddModalOpen(true);
  }, []);

  const filteredUsers = useFilteredUserList(userListHID, searchTerm);
  const totalRecords = filteredUsers.length;
  const paginatedData = filteredUsers.slice(
    (currentPageNumber - 1) * pageSize,
    currentPageNumber * pageSize
  );

  const handleDeleteUser = () => {
    if (!userToDelete) return;
    dispatch(
      deleteUserSagaAction({
        userID: userToDelete.ID,
        setModalOpen: setDeleteModalOpen,
        listType: "hid",
      })
    );
  };

  const handleDeleteModalClose = () => {
    dispatch(userSliceAction.resetDeleteUserStatus());
    setDeleteModalOpen(false);
    setUserToDelete(null);
  };

  const renderModals = () => (
    <>
      {addModalOpen && (
        <HidUserFormModal setModalOpen={setAddModalOpen} mode="Add" />
      )}
      {updateModalOpen && selectedUser && (
        <HidUserFormModal
          user={selectedUser}
          setModalOpen={setUpdateModalOpen}
          mode="Update"
        />
      )}
      {deleteModalOpen && userToDelete && (
        <DeleteModal
          setDeleteOpen={handleDeleteModalClose}
          onDeleteButtonClick={handleDeleteUser}
          deleteButtonLoading={status.deleteUserLoading}
          deleteResultStatus={status.deleteUserStatus}
          deleteResultMessage={status.deleteUserMessage}
          successMessage="HID User Deleted"
          optionalHeader="Delete User Confirmation"
          info={{
            icon: "user",
            name: `${userToDelete.first_name} ${userToDelete.last_name}`,
          }}
          message={`Are you sure you want to delete user ${userToDelete.first_name} ${userToDelete.last_name}? This action cannot be undone.`}
        />
      )}
    </>
  );

  if (status.userListHIDLoading && userListHID.length === 0) {
    return (
      <>
        <div className="um-tab__loading">
          <Loader variant={LoaderSizes.large} />
        </div>
        {renderModals()}
      </>
    );
  }

  if (userListHID.length === 0) {
    return (
      <>
        <div className="um-tab">
          <Notification toasts={toasts} setToasts={setToasts} />
          <div className="um-tab__empty">
            <NoUserStateSVG />
            <p>No HID Users Added</p>
            <Button
              variant="primary"
              label="Add New User"
              className="user-management__tab-action-btn"
              icon={
                <Iconography icon="plus" size={IconographySizes.Medium} />
              }
              onClick={onAddNewUser}
              disabled={readOnly}
            />
          </div>
        </div>
        {renderModals()}
      </>
    );
  }

  return (
    <>
      <div className="um-tab">
        <Notification toasts={toasts} setToasts={setToasts} />
        <UserManagementToolbar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onAddUser={onAddNewUser}
          addButtonDisabled={readOnly}
          searchDisabled={addModalOpen || updateModalOpen}
        />
        <div className="table-card">
          <UserManagementList
            users={paginatedData}
            readOnly={readOnly}
            actions={{
              onEdit: (user) => {
                if (readOnly) return;
                setSelectedUser(user);
                setAddModalOpen(false);
                setUpdateModalOpen(true);
              },
              onDelete: (user) => {
                if (readOnly) return;
                dispatch(userSliceAction.resetDeleteUserStatus());
                setUserToDelete(user);
                setDeleteModalOpen(true);
              },
              onInvite: (user) => {
                if (readOnly) return;
                dispatch(
                  sendAccountInviteSagaAction({
                    ID: user.ID,
                    userEmail: user.email,
                  })
                );
              },
              inviteLoading: status.sendAccountInviteLoading,
            }}
          />
          <Pagination
            currentPageNumber={currentPageNumber}
            totalRecords={totalRecords}
            pageSize={pageSize}
            border={true}
            onPageNumberClick={setCurrentPageNumber}
            onPageSizesClick={(_e, value) => {
              setPageSize(Number(value));
              setCurrentPageNumber(1);
            }}
            pageSizes={[
              { label: "20", value: "20" },
              { label: "50", value: "50" },
              { label: "100", value: "100" },
            ]}
          />
        </div>
      </div>
      {renderModals()}
    </>
  );
};

export default HidUsersTab;
